import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import useSWR from 'swr';
import { Package } from './useData';
import { formatDate } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { Button, PlusIcon, StandardTableActions, Badge } from '../../components/ui';

export interface PackageTasksPanelProps {
  open: boolean;
  onClose: () => void;
  packageItem: Package | null;
  onTaskAdded?: () => void;
  /** When true, render as an inline panel inside the layout (no backdrop, no fixed positioning) */
  asPanel?: boolean;
}

interface LinkedTaskRow {
  id: number;
  taskId: number | null;
  task: {
    taskId: number;
    taskDescription: string | null;
    taskDate: string | null;
    taskInvoicedMin: number | null;
    taskStatusDescr: { ts_descr: string } | null;
  } | null;
}

interface AvailableTask {
  taskId: number;
  taskDescription: string | null;
  taskDate: string | null;
  taskInvoicedMin: number | null;
  taskStatusDescr: { ts_descr: string } | null;
}

function TaskMeta({
  date,
  status,
  invoicedMin,
}: {
  date: string | null;
  status: string | null;
  invoicedMin: number | null;
}) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
      {date && <span title={date}>{formatDate(date)}</span>}
      {status && (
        <Badge variant="accent" className="px-1.5 py-0.5">
          {status}
        </Badge>
      )}
      {invoicedMin != null && <span>{invoicedMin} min invoiced</span>}
    </div>
  );
}

const PackageTasksPanel: React.FC<PackageTasksPanelProps> = ({
  open,
  onClose,
  packageItem,
  onTaskAdded,
  asPanel = false,
}) => {
  const { colors } = useTheme();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [adding, setAdding] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);
  const [unlinkConfirm, setUnlinkConfirm] = useState<LinkedTaskRow | null>(null);

  const packageId = packageItem?.id;
  const { data: linkedTasks = [], mutate: mutateLinked } = useSWR<LinkedTaskRow[]>(
    open && packageId ? `/crm-packages-tasks/all_of/${packageId}` : null,
    fetcher
  );
  const { data: availableTasks = [], mutate: mutateAvailable } = useSWR<AvailableTask[]>(
    open && packageId ? `/crm-packages/${packageId}/available-tasks` : null,
    fetcher
  );

  const toggleSelectTask = (taskId: number) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleConfirmAddTasks = async () => {
    if (!packageId || selectedTaskIds.length === 0) return;
    setAdding(true);
    if (!packageId) return;
    try {
      await api.post('/crm-packages-tasks', { packageId, taskIds: selectedTaskIds });
      setSelectedTaskIds([]);
      setAddModalOpen(false);
      await mutateLinked();
      await mutateAvailable();
      onTaskAdded?.();
    } catch (e: any) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  };

  const handleUnlink = async (linkId: number) => {
    setUnlinkingId(linkId);
    try {
      await api.delete(`/crm-packages-tasks/${linkId}`);
      setUnlinkConfirm(null);
      await mutateLinked();
      await mutateAvailable();
      onTaskAdded?.();
    } catch (e: any) {
      console.error(e);
    } finally {
      setUnlinkingId(null);
    }
  };

  if (!open) return null;

  return (
    <>
      {!asPanel && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={onClose}
          aria-hidden
        />
      )}
      <div
        className={cn(
          'flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700',
          asPanel
            ? 'h-full min-h-0 w-full'
            : cn(
                'fixed top-0 right-0 bottom-0 z-50 w-full max-w-xl',
                'shadow-2xl animate-in slide-in-from-right duration-300'
              )
        )}
        role="dialog"
        aria-label="Linked package tasks"
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Linked tasks
            </h2>
            {packageItem && (
              <>
                <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400 truncate" title={packageItem.packageDescr}>
                  {packageItem.packageDescr || 'Package'}
                </p>
                {packageItem.client?.clientName && (
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                    Client: {packageItem.client.clientName}
                  </p>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={cn('flex-1 overflow-y-auto p-5 space-y-6', asPanel && 'min-h-0')}>
          {/* Add task – same design as Log reminder */}
          <section className="space-y-3">
            <h3 className={cn('text-sm font-semibold uppercase tracking-wide', colors.text)}>
              Add task from this client
            </h3>
            <p className={cn('text-xs', colors.textMuted)}>
              Tasks for this client that are not linked to any package.
            </p>
            {availableTasks.length === 0 ? (
              <div className={cn('rounded-xl border border-dashed p-6 text-center text-sm', colors.border, colors.textMuted)}>
                No unlinked tasks for this client.
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedTaskIds([]);
                  setAddModalOpen(true);
                }}
                className="w-full py-4 border-2 border-dashed"
              >
                <PlusIcon className="w-5 h-5" />
                Select tasks to link
              </Button>
            )}
          </section>

          {/* Linked tasks – cards with View / Edit / Unlink */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
              Linked tasks ({linkedTasks.filter((pt) => pt.task).length})
            </h3>
            {linkedTasks.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No linked tasks yet. Add tasks from the section above.
              </div>
            ) : (
              <ul className="space-y-3">
                {linkedTasks.filter((pt) => pt.task).map((pt) => {
                  const t = pt.task!;
                  const isUnlinking = unlinkingId === pt.id;
                  const showUnlinkConfirm = unlinkConfirm?.id === pt.id;
                  return (
                    <li
                      key={pt.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-4 shadow-sm"
                    >
                      <div className="flex flex-col gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2" title={t.taskDescription ?? undefined}>
                            {t.taskDescription || `Task #${t.taskId}`}
                          </p>
                          <TaskMeta
                            date={t.taskDate}
                            status={t.taskStatusDescr?.ts_descr ?? null}
                            invoicedMin={t.taskInvoicedMin}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <StandardTableActions
                            viewTo={`/crm_tasks/${t.taskId}`}
                            editTo={`/crm_tasks/${t.taskId}/edit`}
                            canDelete={false}
                          />
                          {showUnlinkConfirm ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-xs text-gray-600 dark:text-gray-400">Unlink?</span>
                              <button
                                type="button"
                                disabled={isUnlinking}
                                onClick={() => handleUnlink(pt.id)}
                                className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                                title="Yes, unlink"
                                aria-label="Confirm unlink"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => setUnlinkConfirm(null)}
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                title="No"
                                aria-label="Cancel unlink"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setUnlinkConfirm(pt)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Unlink"
                              aria-label="Unlink task"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Modal: select multiple tasks to link */}
      <Modal
        isOpen={addModalOpen}
        onClose={() => {
          if (!adding) {
            setAddModalOpen(false);
            setSelectedTaskIds([]);
          }
        }}
        size="lg"
      >
        <ModalHeader>
          <ModalTitle>Link tasks to package</ModalTitle>
          <button
            type="button"
            onClick={() => {
              if (!adding) {
                setAddModalOpen(false);
                setSelectedTaskIds([]);
              }
            }}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalContent>
          {availableTasks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No unlinked tasks for this client.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>Select one or more tasks to link to this package.</span>
                <button
                  type="button"
                  className="underline hover:text-gray-800 dark:hover:text-gray-200"
                  onClick={() => {
                    if (selectedTaskIds.length === availableTasks.length) {
                      setSelectedTaskIds([]);
                    } else {
                      setSelectedTaskIds(availableTasks.map((t) => t.taskId));
                    }
                  }}
                >
                  {selectedTaskIds.length === availableTasks.length ? 'Clear selection' : 'Select all'}
                </button>
              </div>
              <ul className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                {availableTasks.map((t) => {
                  const checked = selectedTaskIds.includes(t.taskId);
                  return (
                    <li
                      key={t.taskId}
                      className={cn(
                        'rounded-lg border p-3 flex gap-3 items-start cursor-pointer transition-colors',
                        checked
                          ? 'border-indigo-400 bg-indigo-50/60 dark:border-indigo-600 dark:bg-indigo-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-600'
                      )}
                      onClick={() => toggleSelectTask(t.taskId)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelectTask(t.taskId)}
                        className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2"
                          title={t.taskDescription ?? undefined}
                        >
                          {t.taskDescription || `Task #${t.taskId}`}
                        </p>
                        <TaskMeta
                          date={t.taskDate}
                          status={t.taskStatusDescr?.ts_descr ?? null}
                          invoicedMin={t.taskInvoicedMin}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <button
            type="button"
            disabled={adding}
            onClick={() => {
              if (!adding) {
                setAddModalOpen(false);
                setSelectedTaskIds([]);
              }
            }}
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={adding || selectedTaskIds.length === 0}
            onClick={handleConfirmAddTasks}
            className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            {adding ? 'Linking…' : `Link ${selectedTaskIds.length || ''} task${selectedTaskIds.length === 1 ? '' : 's'}`}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PackageTasksPanel;
